/**
 * useMultiFaceLandmarker — 4꼭지 (Joint Attention) 다중인물 hook.
 *
 * 기존 단일 얼굴 detector(useFaceLandmarker, numFaces:1)와 1인 시험감독을
 * 최적화 유지하되, 별도 hook은 별도 detector 인스턴스를
 * numFaces:2로 설정해 multi-face 검출 구성. JointAttentionDetector 입력 형식에
 * 직접 매핑하는 최소 필드 export 구조: bbox, nose tip, yaw/pitch (transformation matrix
 * decomposition으로 추출, useFaceLandmarker line 967~977 동일 공식).
 */

import { useEffect, useRef, useState } from 'react';
import { FaceTracker } from '../engine/FaceTracker';

export interface MultiFace {
  /** Stable per-frame index id ("face_0", "face_1"). MediaPipe FaceLandmarker
   *  with numFaces:2 returns faces in a consistent order across frames in
   *  practice; for production ID tracking, replace with bbox-IoU matching. */
  id: string;
  /** Face landmark bbox in normalized [0,1] coords. */
  bbox: { x: number; y: number; w: number; h: number };
  /** Nose tip (face mesh idx 1) in normalized coords — gaze ray origin. */
  noseTip: { x: number; y: number };
  /** Head yaw in degrees (+ = looking right). Sign matches useFaceLandmarker. */
  yawDeg: number;
  /** Head pitch in degrees (+ = looking up). */
  pitchDeg: number;
  /** Head roll in degrees. */
  rollDeg: number;
}

export interface UseMultiFaceLandmarkerReturn {
  faces: MultiFace[];
  isReady: boolean;
  fps: number;
}

const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
const NUM_FACES = 2;
const NOSE_TIP_IDX = 1;
const DETECT_INTERVAL_MS = 50; // ~20 fps — multi-face is heavier; cap GPU load.
const DEG = 180 / Math.PI;
const clampN = (v: number, mn: number, mx: number) => Math.max(mn, Math.min(mx, v));

/**
 * Mount once with `enabled=true` to start a numFaces:2 detector against the
 * given video element. Returns up to NUM_FACES faces per frame; empty array
 * when no face is detected.
 */
export function useMultiFaceLandmarker(
  videoElementId: string,
  enabled: boolean,
): UseMultiFaceLandmarkerReturn {
  const [faces, setFaces] = useState<MultiFace[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [fps, setFps] = useState(0);

  const detectorRef = useRef<any>(null);
  const initRef = useRef(false);
  const lastDetectRef = useRef(0);
  const frameCountRef = useRef(0);
  const fpsTRef = useRef(performance.now());
  // Phase 2-B (2026-05-26) — replaces the greedy IoU+immediate-drop logic
  // with FaceTracker: optimal small-N assignment + lost-track memory.
  // Surviving a single-frame occlusion preserves dyadic metrics (JAI/MGI)
  // continuity.
  const trackerRef = useRef(new FaceTracker());

  useEffect(() => {
    if (!enabled || initRef.current) return;
    initRef.current = true;
    let disposed = false;
    let rafId = 0;

    const init = async () => {
      try {
        const vision = await import('@mediapipe/tasks-vision');
        const { FaceLandmarker, FilesetResolver } = vision;
        const fileset = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm',
        );
        const landmarker = await FaceLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numFaces: NUM_FACES,
          outputFacialTransformationMatrixes: true,
          outputFaceBlendshapes: false,
        });
        if (disposed) { landmarker.close?.(); return; }
        detectorRef.current = landmarker;
        setIsReady(true);

        const tick = () => {
          if (disposed) return;
          const videoEl = document.getElementById(videoElementId) as HTMLVideoElement | null;
          if (!videoEl || !videoEl.videoWidth || videoEl.readyState < 2) {
            rafId = requestAnimationFrame(tick);
            return;
          }
          const now = performance.now();
          if (now - lastDetectRef.current < DETECT_INTERVAL_MS) {
            rafId = requestAnimationFrame(tick);
            return;
          }
          lastDetectRef.current = now;

          try {
            const result = detectorRef.current.detectForVideo(videoEl, now);
            const fls = (result.faceLandmarks ?? []) as Array<Array<{ x: number; y: number; z?: number }>>;
            const ms = (result.facialTransformationMatrixes ?? []) as Array<{ data: Float32Array | number[] }>;

            // First pass — compute raw face data without ID yet.
            interface RawFace {
              bbox: { x: number; y: number; w: number; h: number };
              noseTip: { x: number; y: number };
              yawDeg: number; pitchDeg: number; rollDeg: number;
            }
            const raws: RawFace[] = [];
            for (let i = 0; i < fls.length; i++) {
              const lms = fls[i];
              if (!lms || lms.length === 0) continue;
              let minX = 1, minY = 1, maxX = 0, maxY = 0;
              for (const lm of lms) {
                if (lm.x < minX) minX = lm.x;
                if (lm.y < minY) minY = lm.y;
                if (lm.x > maxX) maxX = lm.x;
                if (lm.y > maxY) maxY = lm.y;
              }
              const bbox = {
                x: minX, y: minY,
                w: Math.max(0, maxX - minX),
                h: Math.max(0, maxY - minY),
              };
              const nose = lms[NOSE_TIP_IDX] ?? { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };

              // Euler from transformation matrix — mirror useFaceLandmarker
              // line 967~977 exactly so the yaw/pitch sign convention matches.
              let yawDeg = 0, pitchDeg = 0, rollDeg = 0;
              const tm = ms[i]?.data;
              if (tm && tm.length >= 16) {
                const r00 = tm[0], r10 = tm[1];
                const r20 = tm[2], r21 = tm[6], r22 = tm[10];
                const physYaw = Math.asin(-clampN(r20, -1, 1));
                const physPitch = Math.atan2(r21, r22);
                const physRoll = Math.atan2(r10, r00);
                yawDeg = -physYaw * DEG;
                pitchDeg = -physPitch * DEG;
                rollDeg = physRoll * DEG;
              }
              raws.push({ bbox, noseTip: { x: nose.x, y: nose.y }, yawDeg, pitchDeg, rollDeg });
            }

            // Phase 2-B — delegate stable-ID assignment to FaceTracker
            // (optimal assignment + lost-track reacquisition).
            const snap = trackerRef.current.update(
              raws.map(r => r.bbox), now,
            );
            const out: MultiFace[] = raws.map((raw, i) => ({ id: snap.ids[i], ...raw }));
            setFaces(out);

            frameCountRef.current++;
            if (now - fpsTRef.current >= 1000) {
              setFps(frameCountRef.current);
              frameCountRef.current = 0;
              fpsTRef.current = now;
            }
          } catch {
            // detect errors are non-fatal; next tick retries.
          }
          rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('useMultiFaceLandmarker init failed', err);
      }
    };

    init();

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
      detectorRef.current?.close?.();
      detectorRef.current = null;
      initRef.current = false;
    };
  }, [videoElementId, enabled]);

  return { faces, isReady, fps };
}